import React from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { GripVertical, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Goal {
  id: string;
  name: string;
  goal_type?: string | null;
}

interface PriorityGoalSelectorProps {
  goals: Goal[];
  selectedGoalIds: string[];
  onChange: (goalIds: string[]) => void;
  disabled?: boolean;
}

export const PriorityGoalSelector: React.FC<PriorityGoalSelectorProps> = ({
  goals,
  selectedGoalIds,
  onChange,
  disabled = false,
}) => {
  // Get available goals (not yet selected)
  const availableGoals = goals.filter(goal => !selectedGoalIds.includes(goal.id));
  
  // Get selected goals in priority order
  const selectedGoals = selectedGoalIds
    .map(id => goals.find(g => g.id === id))
    .filter((g): g is Goal => g !== undefined);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || disabled) return;

    const { source, destination } = result;

    // Moving from available to selected
    if (source.droppableId === "available" && destination.droppableId === "selected") {
      const goalId = availableGoals[source.index].id;
      const newSelected = [...selectedGoalIds];
      newSelected.splice(destination.index, 0, goalId);
      onChange(newSelected);
      return;
    }

    // Moving from selected to available (remove)
    if (source.droppableId === "selected" && destination.droppableId === "available") {
      const newSelected = [...selectedGoalIds];
      newSelected.splice(source.index, 1);
      onChange(newSelected);
      return;
    }

    // Reordering within selected
    if (source.droppableId === "selected" && destination.droppableId === "selected") {
      const newSelected = [...selectedGoalIds];
      const [removed] = newSelected.splice(source.index, 1);
      newSelected.splice(destination.index, 0, removed);
      onChange(newSelected);
      return;
    }
  };

  const handleAddGoal = (goalId: string) => {
    if (disabled) return;
    onChange([...selectedGoalIds, goalId]);
  };

  const handleRemoveGoal = (goalId: string) => {
    if (disabled) return;
    onChange(selectedGoalIds.filter(id => id !== goalId));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Available Goals */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Available Goals</h4>
            <Badge variant="secondary" className="text-xs">
              {availableGoals.length}
            </Badge>
          </div>
          <Droppable droppableId="available">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border rounded-lg p-3 min-h-[200px] max-h-[300px] overflow-y-auto space-y-2 transition-colors ${
                  snapshot.isDraggingOver ? 'bg-muted/50' : 'bg-background'
                }`}
              >
                {availableGoals.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    All goals selected
                  </p>
                ) : (
                  availableGoals.map((goal, index) => (
                    <Draggable
                      key={goal.id}
                      draggableId={`available-${goal.id}`}
                      index={index}
                      isDragDisabled={disabled}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`flex items-center gap-2 p-2 rounded border bg-card transition-all ${
                            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : 'hover:bg-muted/50'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab'}`}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{goal.name}</p>
                            {goal.goal_type && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {goal.goal_type}
                              </Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddGoal(goal.id);
                            }}
                            disabled={disabled}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Selected Goals (Ordered) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Selected Goals (Ordered)</h4>
            <Badge variant="secondary" className="text-xs">
              {selectedGoals.length}
            </Badge>
          </div>
          <Droppable droppableId="selected">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border rounded-lg p-3 min-h-[200px] max-h-[300px] overflow-y-auto space-y-2 transition-colors ${
                  snapshot.isDraggingOver ? 'bg-primary/5 border-primary' : 'bg-muted/20'
                }`}
              >
                {selectedGoals.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Drag goals here or click + to add
                  </p>
                ) : (
                  selectedGoals.map((goal, index) => (
                    <Draggable
                      key={goal.id}
                      draggableId={`selected-${goal.id}`}
                      index={index}
                      isDragDisabled={disabled}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`flex items-center gap-2 p-2 rounded border bg-card transition-all ${
                            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : 'hover:bg-muted/50'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab'}`}
                        >
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{goal.name}</p>
                            {goal.goal_type && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {goal.goal_type}
                              </Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10 hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveGoal(goal.id);
                            }}
                            disabled={disabled}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {selectedGoals.length > 0 && (
            <p className="text-xs text-muted-foreground">
              💡 Drag to reorder priority (1 = highest)
            </p>
          )}
        </div>
      </div>
    </DragDropContext>
  );
};
